import { Call, Device } from '@twilio/voice-sdk';
import { getTwilioToken } from 'shared/api';
import { EventEmitter } from 'events';
import { makeLog } from 'shared/utils/log';

const log = makeLog('OutboundTwilio');

export class OutboundTwilio extends EventEmitter {
  device: Device | null;
  call: Call | null;

  constructor() {
    super();
    this.device = null;
    this.call = null;
  }

  async init() {
    const {
      data: { token },
    } = await getTwilioToken();

    this.device = new Device(token, {
      logLevel: 1,
      codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      closeProtection: true,
    });
    this.device.on('registered', () => this.emit('device.registered'));
    this.device.on('error', e => this.emit('device.error', e));
    this.device.register();
  }

  muteIncomingAudio(muted: boolean) {
    log.assert(this.call !== null, 'mute() called before init');
    this.call?.mute(muted);
  }

  muteOutgoingAudio(muted: boolean) {
    this.device?.audio?.inputStream?.getAudioTracks().forEach(track => {
      track.enabled = !muted;
    });
  }

  setInputDevice(id: string) {
    log.assert(this.device !== null, 'setInputDevice() called before init');
    this.device!.audio?.setInputDevice(id);
  }

  async connect(params: Record<string, string>) {
    log.assert(this.device !== null, 'connect() called w/o device');
    this.call = await this.device!.connect({ params });
    this.call.on('accept', () => {
      this.emit('session.accept');
    });
    this.call.on('cancel', () => {
      this.emit('session.cancel');
    });
    this.call.on('disconnect', () => {
      this.emit('session.disconnect');
    });
    this.call.on('error', e => {
      this.emit('session.error', e);
    });
    this.call.on('reject', () => {
      this.emit('session.reject');
    });
  }

  async destroy() {
    log.debug('Destroying Twilio instance.');
    log.assert(this.device !== null, 'destroy() called w/o device');
    if (this.call) {
      await this.disconnectAndDestroy();
    } else {
      this.device?.audio?.unsetInputDevice();
      this.device?.destroy();
    }
  }

  // XREF: https://servicebell-ld.sentry.io/issues/4997062837/events/190b5391a52e4ef8b827a0c18015081e/
  private async disconnectAndDestroy() {
    log.debug('Disconnecting call before destroying.');
    const call = this.call!;
    const device = this.device!;
    const disconnected = new Promise(resolve => {
      if (call.status() !== 'closed') {
        call.on('disconnect', resolve);
        setTimeout(resolve, 1500);
      } else {
        resolve(null);
      }
    });
    call.disconnect();
    await disconnected;
    log.debug('Call disconnected. Destroying Twilio instance.');
    device.audio?.unsetInputDevice();
    device.destroy();
  }
}
